import { useUrlContext } from '@resistapp/client/contexts/url-provider';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useProjects } from '@resistapp/client/hooks/api';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import { shortStatus } from '@resistapp/common/friendly';
import { Project } from '@resistapp/common/types';
import { chakraComponents, type OptionProps } from 'chakra-react-select';
import { MouseEvent, useCallback, useMemo } from 'react';
import { getProjectNavigationPage } from '../../utils/project-page-access';
import { ResistomapSelect } from '../forms/resistomap-select';
import { dotStylesForSelect } from '../shared/general-components';
import { theme } from '../shared/theme';

interface Props {
  showStatus?: boolean;
  disableNavigation?: boolean;
  onProjectSelect?: (projectId: number, project: Project) => void;
  value?: number;
  maxWidth?: string;
  maxHeight?: string;
  minWidth?: string;
  height?: string;
  menuHeight?: string;
}

type NavigationTarget = {
  href: string;
  useNavigate: boolean;
};

type ProjectOption = {
  label: string;
  value: number;
  searchableLabel: string;
  project: Project;
  navigationTarget: NavigationTarget | null;
};

export const ProjectSelector = ({
  showStatus,
  disableNavigation,
  onProjectSelect,
  value,
  maxWidth,
  maxHeight,
  minWidth,
  height,
  menuHeight,
}: Props) => {
  const navigate = useNavigateWithQuery();
  const { data: projects } = useProjects();
  const { page, projectId } = useUrlContext();
  const { user, isAdmin: isAdminUser } = useUser();

  const pageCurrent = page.current;

  const getNavigationTarget = useCallback(
    (projectIdLocal: number): NavigationTarget => {
      const targetPage = getProjectNavigationPage({
        user,
        currentPage: pageCurrent,
        projectId: projectIdLocal,
      });

      return {
        href: `/${targetPage}/${projectIdLocal}`,
        useNavigate: pageCurrent === 'research' || pageCurrent === 'admin',
      };
    },
    [pageCurrent, user],
  );

  // This should redirect user so that:
  // - When they are on the pages that support projectId, they stay on the same page, but project changes
  // - When they are on the pages that don't support projectId, they are redirected to the correct page
  //    - In the priority order, if user has access to the pages: index-page -> risk-page -> reduction-page
  //    - If user doesn't have access to any of the pages, they are redirected to the research page
  const handleSelect = useCallback(
    (projectIdLocal: number, project: Project) => {
      if (disableNavigation && onProjectSelect) {
        onProjectSelect(projectIdLocal, project);
        return;
      }

      const navigationTarget = getNavigationTarget(projectIdLocal);

      if (navigationTarget.useNavigate) {
        navigate(navigationTarget.href, false);
        return;
      }

      // TODO: Use navigate
      window.location.href = navigationTarget.href;
    },
    [disableNavigation, getNavigationTarget, navigate, onProjectSelect],
  );

  const options = useMemo(
    () =>
      (projects
        ?.map<ProjectOption>(project => ({
          label: project.name,
          value: project.id,
          searchableLabel: `${project.id} ${project.name} ${new Date(project.createdAt).toLocaleDateString()}`,
          project,
          navigationTarget: disableNavigation ? null : getNavigationTarget(project.id),
        }))
        .reverse() || []),
    [disableNavigation, getNavigationTarget, projects],
  );

  const projectIdValue = projectId.current;
  const defaultValue = useMemo(() => {
    if (value !== undefined) {
      return options.find(projectLocal => projectLocal.value === value);
    }
    return options.find(projectLocal => projectLocal.value === projectIdValue);
  }, [options, value, projectIdValue]);

  return (
    <ResistomapSelect<ProjectOption, false>
      options={options}
      placeholder="Select a project"
      onChange={option => {
        option && handleSelect(option.value, option.project);
      }}
      value={defaultValue}
      filterOption={(option, inputValue) => {
        const searchValue = inputValue.toLowerCase();
        return option.data.searchableLabel.toLowerCase().includes(searchValue);
      }}
      formatOptionLabel={option => option.label}
      chakraStyles={{
        container: styles => {
          return {
            ...styles,
            // width: minWidth && maxWidth ? 'max-content' : '100%',
            width: minWidth ? 'auto' : '100%', // Default to 100% width if no minWidth is specified
            maxWidth: maxWidth || '100%',
            minWidth: minWidth || '100%',
            maxHeight: maxHeight || 'auto',

            fontWeight: theme.fontWeight.light,
            fontSize: theme.fontSize.medium,
            color: theme.colors.neutral700,
          };
        },
        control: styles => {
          return {
            ...styles,
            border: theme.borders.normal,
            backgroundColor: 'white',
            borderRadius: theme.borders.radius.full,
            height: height || 'auto',
            minHeight: height || styles.minHeight,
          };
        },
        valueContainer: styles => {
          return {
            ...styles,
            height: height || styles.height,
          };
        },
        menu: styles => {
          return {
            ...styles,
            maxHeight: menuHeight || styles.maxHeight,
          };
        },
        menuList: styles => {
          return {
            ...styles,
            maxHeight: menuHeight || styles.maxHeight,
          };
        },
        option: (styles, { data }) => {
          if (!showStatus) {
            return {
              ...styles,
            };
          }

          return {
            ...styles,
            ...dotStylesForSelect(isAdminUser ? shortStatus(data.project.type) : '-', data.project.userAccessCount),
            position: 'relative',
            whiteSpace: 'nowrap',
          };
        },
      }}
      components={{
        Option: ProjectSelectorOption,
      }}
    />
  );
};

function ProjectSelectorOption(props: OptionProps<ProjectOption, false>) {
  const { data, innerProps } = props;
  const { navigationTarget } = data;

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    const shouldOpenInNewTab = Boolean(navigationTarget) && (event.metaKey || event.ctrlKey || event.button === 1);

    if (shouldOpenInNewTab && navigationTarget) {
      event.preventDefault();
      event.stopPropagation();
      window.open(navigationTarget.href, '_blank', 'noopener,noreferrer');
      return;
    }

    innerProps.onMouseDown?.(event);
  };

  return (
    <chakraComponents.Option
      {...props}
      innerProps={{
        ...innerProps,
        onMouseDown: handleMouseDown,
      }}
    />
  );
}
